import { Box, Chip, NewMenu } from '@common-components';
import {
  Close as CloseIcon,
  ContactSupportOutlined as ContactSupportOutlinedIcon,
  MoreHoriz as MoreHorizIcon,
} from '@icons';
import { useOptionsMenu } from 'hooks';
import { useMutateSubmissionEmail } from 'hooks/api/submissionEmail';
import { useMutateSubmissionInboundEmail } from 'hooks/api/submissionInboundEmail';
import { EmailItemType } from 'broker/components/Emails/types';
import useSingleFieldOptimisticUpdate from 'broker/hooks/useSingleFieldOptimisticUpdate';
import { EmailListItemAttributes } from './utils';

export interface InformationRequestChipProps {
  email: EmailListItemAttributes;
  emailType: EmailItemType;
}

export default function InformationRequestChip({ email, emailType }: InformationRequestChipProps) {
  const optionsMenu = useOptionsMenu();
  const { updateSubmissionInboundEmail } = useMutateSubmissionInboundEmail();
  const { updateSubmissionEmail } = useMutateSubmissionEmail();

  const currentInformationRequestData = email.labels?.informationRequestData;

  const updateOutboundEmail = async (informationRequestData: { value: boolean; isAIExtracted: boolean }) => {
    await updateSubmissionEmail.mutateAsync({
      id: email.id,
      data: {
        labels: {
          ...email.labels,
          informationRequestData,
        },
      },
    });
  };

  const updateInboundEmail = async (informationRequestData: { value: boolean; isAIExtracted: boolean }) => {
    if (!email.submissionInboundEmailId) {
      return;
    }
    await updateSubmissionInboundEmail.mutateAsync({
      id: email.submissionInboundEmailId,
      data: {
        labels: {
          ...email.labels,
          informationRequestData,
        },
      },
    });
  };

  const { value: informationRequestData, onChange: onInformationRequestChange } = useSingleFieldOptimisticUpdate<{
    value: boolean;
    isAIExtracted: boolean;
  }>({
    serverValue: currentInformationRequestData,
    apiUpdate: emailType === EmailItemType.SubmissionOutbound ? updateOutboundEmail : updateInboundEmail,
  });

  const hasInformationRequested = !!informationRequestData?.value;
  const isAIExtracted = !!informationRequestData?.isAIExtracted;
  const icon = hasInformationRequested ? ContactSupportOutlinedIcon : MoreHorizIcon;
  const color = isAIExtracted ? 'green.100' : 'secondary';

  const onInformationRequestClick = async () => {
    optionsMenu.closeMenu();
    if (hasInformationRequested) {
      return;
    }
    await onInformationRequestChange({ value: true, isAIExtracted: false });
  };

  const onClearInformationRequest = async () => {
    await onInformationRequestChange({ value: false, isAIExtracted: false });
  };

  return (
    <Box>
      <Chip startIcon={icon} size="small" onClick={optionsMenu.openMenu} rounded color={color} />
      <NewMenu
        optionsMenuState={optionsMenu}
        menuItems={[
          {
            key: 'information-requests',
            items: [
              {
                startAdornment: ContactSupportOutlinedIcon,
                label: 'Information Requests',
                onClick: onInformationRequestClick,
              },
              {
                startAdornment: CloseIcon,
                label: 'Clear',
                onClick: onClearInformationRequest,
              },
            ],
          },
        ]}
      />
    </Box>
  );
}
